// StudentList.js
import React, { useState } from 'react';
import {
    Box,
    List,
    ListItem,
    ListItemText,
    Typography,
    Chip,
    Button,
    Divider,
    Paper,
    Tooltip,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    CircularProgress,
    Alert
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import LockResetIcon from '@mui/icons-material/LockReset';
import ExportSummaryModal from './ExportSummaryModal';
import { firebaseService } from '../utils/FirebaseService';

const formatStudentName = (email) => {
    if (email.endsWith('@classroom.meld')) {
        // Extract first and last name from classroom.meld email
        const [firstName, lastName] = email.split('@')[0].split('.').slice(0, 2);
        return `${firstName.charAt(0).toUpperCase() + firstName.slice(1)} ${lastName.charAt(0).toUpperCase() + lastName.slice(1)}`;
    }
    return email;
};

const StudentList = ({
                         roster,
                         studentStatuses,
                         activeUsers,
                         registeredUsers,
                         selectedMEL,
                         handleMELSelect,
                         handleExportSummary,
                         currentModel,
                         userEmailToId
                     }) => {
    const [isExportModalOpen, setIsExportModalOpen] = useState(false);
    const [resetDialog, setResetDialog] = useState({ open: false, email: '' });
    const [newPassword, setNewPassword] = useState('');
    const [resetLoading, setResetLoading] = useState(false);
    const [resetError, setResetError] = useState('');
    const [resetSuccess, setResetSuccess] = useState(false);

    const handleResetOpen = (email) => {
        setResetDialog({ open: true, email });
        setNewPassword('');
        setResetError('');
        setResetSuccess(false);
    };

    const handleResetClose = () => {
        setResetDialog({ open: false, email: '' });
    };

    const handleResetSubmit = async () => {
        setResetError('');
        setResetLoading(true);
        try {
            await firebaseService.resetStudentPassword(resetDialog.email, newPassword);
            setResetSuccess(true);
        } catch (err) {
            setResetError(err.message);
        } finally {
            setResetLoading(false);
        }
    };

    const getEmailStyle = (email) => {
        const lowercaseEmail = email.toLowerCase();
        const isActive = activeUsers.has(lowercaseEmail);
        const isRegistered = registeredUsers.has(lowercaseEmail);

        if (!isRegistered) {
            return {
                color: 'text.disabled',
                tooltip: 'Not Registered'
            };
        }

        if (!isActive) {
            return {
                color: 'text.primary',
                tooltip: 'Not Logged In'
            };
        }

        return {
            fontWeight: 'bold',
            color: 'text.primary',
            tooltip: 'Present'
        };
    };

    const handleExportClick = () => {
        setIsExportModalOpen(true);
    };

    const handleFilteredExport = (roster, selectedStudents) => {
        handleExportSummary(roster, selectedStudents);
    };

    return (
        <Paper variant="outlined" sx={{ maxHeight: 400, overflow: 'auto' }}>
            <Box>
                <List dense>
                    {roster.students?.map((email, index) => {
                        const style = getEmailStyle(email);
                        const hasModel = studentStatuses[email.toLowerCase()]?.hasModel;
                        const isSelected = selectedMEL === email;
                        const displayName = formatStudentName(email);

                        return (
                            <React.Fragment key={index}>
                                <ListItem
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        gap: 1
                                    }}
                                >
                                    <Tooltip title={style.tooltip}>
                                        <ListItemText
                                            primary={
                                                <Typography sx={{ ...style }}>
                                                    {displayName}
                                                </Typography>
                                            }
                                        />
                                    </Tooltip>
                                    {hasModel && (
                                        <Chip
                                            label="MEL"
                                            size="small"
                                            onClick={() => handleMELSelect(email)}
                                            sx={{
                                                bgcolor: isSelected ? 'warning.main' : 'grey.300',
                                                color: isSelected ? 'warning.contrastText' : 'text.primary',
                                                '&:hover': {
                                                    bgcolor: isSelected ? 'warning.dark' : 'grey.400'
                                                },
                                                minWidth: '60px',
                                                cursor: 'pointer'
                                            }}
                                        />
                                    )}
                                    {email.endsWith('@classroom.meld') && (
                                        <Tooltip title="Reset password">
                                            <IconButton
                                                size="small"
                                                onClick={() => handleResetOpen(email)}
                                                sx={{ color: 'text.secondary' }}
                                            >
                                                <LockResetIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </ListItem>
                                {index < roster.students.length - 1 && <Divider />}
                            </React.Fragment>
                        );
                    })}
                </List>

                <Button
                    variant="outlined"
                    size="small"
                    fullWidth
                    sx={{ mt: 2 }}
                    onClick={handleExportClick}
                    startIcon={<CloudUploadIcon />}
                >
                    Export Summary
                </Button>
            </Box>

            <ExportSummaryModal
                isOpen={isExportModalOpen}
                onClose={() => setIsExportModalOpen(false)}
                roster={roster}
                studentStatuses={studentStatuses}
                onExport={handleFilteredExport}
                currentModel={currentModel}
                userEmailToId={userEmailToId}
            />

            <Dialog open={resetDialog.open} onClose={handleResetClose} maxWidth="xs" fullWidth>
                <DialogTitle>Reset Password</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        Set a new password for <strong>{resetDialog.email ? formatStudentName(resetDialog.email) : ''}</strong>.
                        Inform the student of the new password privately.
                    </Typography>
                    {resetSuccess ? (
                        <Alert severity="success">Password updated successfully.</Alert>
                    ) : (
                        <>
                            {resetError && <Alert severity="error" sx={{ mb: 1 }}>{resetError}</Alert>}
                            <TextField
                                label="New Password"
                                type="password"
                                fullWidth
                                size="small"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && !resetLoading && handleResetSubmit()}
                                helperText="Minimum 6 characters"
                                autoFocus
                            />
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleResetClose}>
                        {resetSuccess ? 'Close' : 'Cancel'}
                    </Button>
                    {!resetSuccess && (
                        <Button
                            onClick={handleResetSubmit}
                            variant="contained"
                            disabled={newPassword.length < 6 || resetLoading}
                            startIcon={resetLoading ? <CircularProgress size={16} /> : null}
                        >
                            Reset Password
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        </Paper>
    );
};

export default StudentList;